import { MutableRefObject, useCallback, useMemo, useRef } from 'react';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import { useTheme } from 'styled-components';
import waveformAnimation from '../../assets/animations/voice-waveform.json';
import {
  WAVEFORM_BAR_HEIGHTS,
  WAVEFORM_LOTTIE_GROUPS,
  WAVEFORM_LOTTIE_REST_FRAME,
  WAVEFORM_LOTTIE_TOTAL_FRAMES,
  WAVEFORM_LOTTIE_VIEW_BOX_SIZE,
  WAVEFORM_MAX_SPEED,
  WAVEFORM_MIN_SPEED,
} from './constants';
import { applyLottieColor } from './lottie-color.helper';
import { useAudioLevel } from './use-audio-level.hook';
import { useReducedMotion } from './use-reduced-motion.hook';
import { LottieBarGroup, WaveBar, Waveform } from './styles';

interface Props {
  isWarning?: boolean;
  isRecording?: boolean;
  stream?: MediaStream | null;
}

const RENDERER_SETTINGS = {
  viewBoxOnly: true,
  viewBoxSize: WAVEFORM_LOTTIE_VIEW_BOX_SIZE,
};

const GROUP_FRAME_OFFSET =
  WAVEFORM_LOTTIE_TOTAL_FRAMES / WAVEFORM_LOTTIE_GROUPS;

const restFrameFor = (index: number) =>
  WAVEFORM_LOTTIE_REST_FRAME + index * GROUP_FRAME_OFFSET;

const RecordingWaveform = ({ isWarning, isRecording, stream }: Props) => {
  const theme = useTheme();
  const isReducedMotion = useReducedMotion();
  const groupRefs = useRef(
    Array.from(
      { length: WAVEFORM_LOTTIE_GROUPS },
      () =>
        ({ current: null }) as MutableRefObject<LottieRefCurrentProps | null>
    )
  ).current;
  const isPlayingRef = useRef(false);

  const barColor = isWarning
    ? theme.colors.baseColors.red100
    : theme.colors.baseColors.blue100;
  const animationData = useMemo(
    () => applyLottieColor(waveformAnimation, barColor),
    [barColor]
  );
  const isAnimated = Boolean(isRecording) && !isReducedMotion;

  const handleLevel = useCallback(
    (level: number) => {
      const shouldPlay = level > 0;

      groupRefs.forEach((groupRef, index) => {
        const group = groupRef.current;

        if (!group) return;

        if (!shouldPlay) {
          if (isPlayingRef.current) {
            group.goToAndStop(restFrameFor(index), true);
          }
          return;
        }

        group.setSpeed(
          WAVEFORM_MIN_SPEED + level * (WAVEFORM_MAX_SPEED - WAVEFORM_MIN_SPEED)
        );

        if (!isPlayingRef.current) group.play();
      });

      isPlayingRef.current = shouldPlay;
    },
    [groupRefs]
  );

  useAudioLevel({
    stream: stream ?? null,
    onLevel: handleLevel,
    isEnabled: isAnimated,
  });

  if (isAnimated) {
    return (
      <Waveform aria-hidden='true'>
        {groupRefs.map((groupRef, index) => (
          <LottieBarGroup
            // eslint-disable-next-line react/no-array-index-key
            key={index}>
            <Lottie
              lottieRef={groupRef}
              animationData={animationData}
              rendererSettings={RENDERER_SETTINGS}
              onDOMLoaded={() => {
                isPlayingRef.current = false;
                groupRef.current?.goToAndStop(restFrameFor(index), true);
              }}
            />
          </LottieBarGroup>
        ))}
      </Waveform>
    );
  }

  return (
    <Waveform aria-hidden='true'>
      {WAVEFORM_BAR_HEIGHTS.map((height, index) => (
        <WaveBar
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          $height={height}
          $isWarning={isWarning}
        />
      ))}
    </Waveform>
  );
};

export default RecordingWaveform;
